import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin'
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import React, { FC, useState, useMemo, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { $convertFromMarkdownString } from '@lexical/markdown'
import { Box, useTheme, Typography } from '@mui/material'
import { LexicalEditor } from 'lexical'

import { useSharedHistoryContext, SharedHistoryContext } from './context/SharedHistoryContext'
import { DraggableBlockPlugin } from './plugins/DraggableBlockPlugin'
import { ImagesPlugin } from './plugins/ImagesPlugin'
import { LinkPlugin } from './plugins/LinkPlugin'
import { MarkdownShortcutPlugin } from './plugins/MarkdownShortcutPlugin'
import { TableActionMenuPlugin } from './plugins/TableActionMenuPlugin'
import { FocusPlugin } from './plugins/FocusPlugin'
import { ToolbarPlugin } from './plugins/ToolbarPlugin'
import { LexicalContentEditable } from './ui/ContentEditable'
import { Placeholder } from './ui/Placeholder'
import { FloatingLinkEditorPlugin } from './plugins/FloatingLinkEditorPlugin'
import { FloatingTextFormatToolbarPlugin } from './plugins/FloatingTextFormatToolbarPlugin'
import { ALL_TRANSFORMERS } from './plugins/MarkdownTransformers'
import { TableContext } from './plugins/TablePlugin'
import { OnChangePlugin } from './plugins/OnChangePlugin'
import AllNodes from './nodes/AllNodes'
import { useNodesStyles, nodesClasses } from './styles'
import { NotificationImageUploadResponse } from '@e3dc-react-apps/portal/src/Libs/notifications/models/notifications.model'

export interface MarkdownEditorProps {
  onChange: (value: string) => void
  value: string
  label?: string
  error?: boolean
  helperText?: string
  onImageDropped?: (image: File | undefined) => Promise<NotificationImageUploadResponse | undefined>
  isLoading?: boolean
}

/**
 * This reused component gives opportunity creating/editing markdown format
 */
const MarkdownEditor: FC<MarkdownEditorProps> = ({ onChange, value, label, error, helperText, onImageDropped, isLoading }) => {
  const initialValue = useRef(value)
  const theme = useTheme()
  const { classes } = useNodesStyles()
  const { historyState } = useSharedHistoryContext()
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null)
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false)
  const [isFocused, setFocused] = useState(false)

  const onRef = (_floatingAnchorElem: HTMLDivElement): void => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem)
    }
  }

  const initialConfig = {
    namespace: 'Markdown Editor',
    nodes: AllNodes,
    onError: (error: Error) => {
      throw error
    },
    theme: nodesClasses,
    editorState: (editor: LexicalEditor) => {
      editor.update(() => {
        $convertFromMarkdownString(initialValue.current || '', ALL_TRANSFORMERS)
      })
    },
  }

  const editorShellClass = useMemo(() => {
    if (isFocused && !error) {
      return `${classes.editorShell} ${classes.editorShellFocused}`
    }

    if (error) {
      return `${classes.editorShell} ${classes.editorShellError}`
    }

    return classes.editorShell
  }, [error, isFocused, classes])

  const toolbarSx = useMemo(() => {
    if (isFocused && !error) {
      return {
        borderBottom: `1px solid ${theme.shape.inputField.labelColorActive}`,
      }
    }

    if (error) {
      return {
        borderBottom: `1px solid ${theme.palette.error.main}`,
      }
    }

    return {
      borderBottom: `1px solid ${theme.palette.buttonGroup.borderColor}`,
    }
  }, [error, isFocused, theme])

  return (
    <Box className={classes.mainContainer}>
      <LexicalComposer initialConfig={initialConfig}>
        <SharedHistoryContext>
          <TableContext>
            <Box className={editorShellClass}>
              <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} sx={toolbarSx} onImageDropped={onImageDropped} isLoading={isLoading} />
              <Box className={classes.editorContainer}>
                <FocusPlugin setFocus={setFocused} />
                <ClearEditorPlugin />
                <HistoryPlugin externalHistoryState={historyState} />
                <RichTextPlugin
                  contentEditable={
                    <Box className={classes.editorScroller}>
                      <Box className={classes.editor} ref={onRef}>
                        <LexicalContentEditable />
                      </Box>
                    </Box>
                  }
                  placeholder={<Placeholder error={error}>{label}</Placeholder>}
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <MarkdownShortcutPlugin />
                <ListPlugin />
                <CheckListPlugin />
                <TablePlugin hasCellBackgroundColor={true} />
                <ImagesPlugin />
                <LinkPlugin />
                <TabIndentationPlugin />
                {floatingAnchorElem && (
                  <>
                    <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
                    <FloatingLinkEditorPlugin isLinkEditMode={isLinkEditMode} setIsLinkEditMode={setIsLinkEditMode} />
                    <TableActionMenuPlugin anchorElem={floatingAnchorElem} />
                    <FloatingTextFormatToolbarPlugin />
                  </>
                )}
                <OnChangePlugin onChange={value => onChange(value)} />
              </Box>
            </Box>
          </TableContext>
        </SharedHistoryContext>
      </LexicalComposer>
      {error && (
        <Typography variant="caption" className={classes.errorMessage}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}

export default MarkdownEditor
